import React, { useEffect, useMemo, useState } from 'react';
import { useLengthUnits, normalizeUnitsData } from '../../hooks/queries/useLengthUnits';
import { useWeightUnits } from '../../hooks/queries/useWeightUnits';
import {
  STANDARD_WEIGHT_SYSTEM_OPTIONS,
  inferStandardWeightSystem,
  lookupUnitLabel,
  resolveStandardWeightUnits,
} from '../../utils/standardWeightUnits';

const READONLY_FIELD_STYLE = {
  border: '1px solid var(--wf-input-border, #dee2e6)',
  borderRadius: '8px',
  height: '45px',
  fontSize: '0.9rem',
  backgroundColor: 'var(--wf-input-bg, #f3f4f6)',
  color: 'var(--wf-input-text, inherit)',
};

const SELECT_FIELD_STYLE = {
  border: '1px solid var(--wf-input-border, #dee2e6)',
  borderRadius: '8px',
  height: '45px',
  fontSize: '0.9rem',
  backgroundColor: 'var(--wf-input-bg, #fafafa)',
  color: 'var(--wf-input-text, inherit)',
};

const LABEL_STYLE = { color: 'var(--wf-label, #6c757d)', fontSize: '0.85rem' };

/**
 * Standard-weight system picker (metric vs English) with read-only length/weight units.
 * Parent still stores Weight_Unit / Length_Unit IDs on the pond detail record.
 */
const PondDetailStandardWeightUnits = ({
  weightUnitId = '',
  lengthUnitId = '',
  onUnitsChange,
  systemError = '',
  unitsError = '',
  disabled = false,
  idPrefix = 'pondDetail',
}) => {
  const { data: lengthData, isLoading: lengthLoading } = useLengthUnits();
  const { data: weightData, isLoading: weightLoading } = useWeightUnits({ search: '' });

  const lengthUnits = useMemo(() => normalizeUnitsData(lengthData), [lengthData]);
  const weightUnits = useMemo(() => normalizeUnitsData(weightData), [weightData]);

  const [system, setSystem] = useState('');

  const unitsReady = !lengthLoading && !weightLoading && lengthUnits.length > 0 && weightUnits.length > 0;

  useEffect(() => {
    if (!unitsReady) return;
    if (!weightUnitId || !lengthUnitId) {
      setSystem('');
      return;
    }
    const inferred = inferStandardWeightSystem(
      lengthUnitId,
      weightUnitId,
      lengthUnits,
      weightUnits
    );
    setSystem(inferred);
  }, [unitsReady, weightUnitId, lengthUnitId, lengthUnits, weightUnits]);

  const resolved = useMemo(() => {
    if (!system || !unitsReady) return null;
    return resolveStandardWeightUnits(system, lengthUnits, weightUnits);
  }, [system, unitsReady, lengthUnits, weightUnits]);

  const lengthLabel =
    resolved?.lengthUnitLabel ||
    lookupUnitLabel(lengthUnits, lengthUnitId) ||
    '—';
  const weightLabel =
    resolved?.weightUnitLabel ||
    lookupUnitLabel(weightUnits, weightUnitId) ||
    '—';

  const missingUnitsMessage = useMemo(() => {
    if (!system || !unitsReady) return '';
    if (resolved) return '';
    return system === 'metric'
      ? 'Metric units (mm and gram) were not found in the unit list. Contact an administrator.'
      : 'English units (inch and pound) were not found in the unit list. Contact an administrator.';
  }, [system, unitsReady, resolved]);

  const handleSystemChange = (e) => {
    const nextSystem = e.target.value;
    setSystem(nextSystem);
    if (!nextSystem) {
      onUnitsChange?.({ system: '', weightUnitId: '', lengthUnitId: '' });
      return;
    }
    const next = resolveStandardWeightUnits(nextSystem, lengthUnits, weightUnits);
    if (!next) {
      onUnitsChange?.({ system: nextSystem, weightUnitId: '', lengthUnitId: '' });
      return;
    }
    onUnitsChange?.({
      system: nextSystem,
      weightUnitId: String(next.weightUnitId),
      lengthUnitId: String(next.lengthUnitId),
    });
  };

  const selectInvalid = !!(systemError || unitsError || missingUnitsMessage);

  const systemSelectId = `${idPrefix}StandardWeightSystem`;
  const weightDisplayId = `${idPrefix}WeightUnitDisplay`;
  const lengthDisplayId = `${idPrefix}LengthUnitDisplay`;

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="form-floating mb-0">
          <select
            className={`form-control ${selectInvalid ? 'is-invalid' : ''}`}
            id={systemSelectId}
            value={system}
            onChange={handleSystemChange}
            disabled={disabled || !unitsReady}
            style={SELECT_FIELD_STYLE}
          >
            <option value="">Select standard weight system</option>
            {STANDARD_WEIGHT_SYSTEM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label htmlFor={systemSelectId} style={LABEL_STYLE}>
            Standard weight system <span style={{ color: '#dc3545' }}>*</span>
          </label>
          {systemError && <div className="invalid-feedback d-block">{systemError}</div>}
          {unitsError && <div className="invalid-feedback d-block">{unitsError}</div>}
          {missingUnitsMessage && (
            <div className="invalid-feedback d-block">{missingUnitsMessage}</div>
          )}
        </div>
        <p className="small text-muted mb-0 mt-2 donezo-page-fine-print">
          Metric uses mm and gram (Ws mm &amp; gram). English uses inch and pound (Ws inch &amp;
          pound). Length and weight units below are set automatically.
        </p>
      </div>

      <div className="col-12 col-md-6">
        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id={weightDisplayId}
            value={weightLabel}
            readOnly
            tabIndex={-1}
            style={READONLY_FIELD_STYLE}
            aria-readonly="true"
          />
          <label htmlFor={weightDisplayId} style={LABEL_STYLE}>
            Weight unit
          </label>
        </div>
      </div>

      <div className="col-12 col-md-6">
        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id={lengthDisplayId}
            value={lengthLabel}
            readOnly
            tabIndex={-1}
            style={READONLY_FIELD_STYLE}
            aria-readonly="true"
          />
          <label htmlFor={lengthDisplayId} style={LABEL_STYLE}>
            Length unit
          </label>
        </div>
      </div>
    </div>
  );
};

export default PondDetailStandardWeightUnits;
